import { Box, Button, HStack, Icon, Text } from "@chakra-ui/react";
import CollapseAnimation from "@raiden/library/components/Animations/Collapse";
import CustomersDocumentsTable from "@raiden/library/containers/CustomersDocuments/Table";
import useRouterNumber from "@raiden/library/hooks/router/useRouterNumber";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";
import { useIntl } from "react-intl";
import ContentBox from "../../components/ContentBox";
import { useHeader } from "../../hooks/useHeader";

const MAX_DOCUMENTS_INITIALLY_DISPLAYED = 4;

/**
 * @typedef {object} Props
 * @property {string} title
 * @property {number} userId
 * @property {import("@raiden/library/types/Document").Document[]} documents
 * @property {import("@raiden/library/types/CustomerDocument").CustomerDocument[]} customerDocuments
 * @property {() => void} mutateCustomerDocuments
 * @property {import("@raiden/library/types/Holder").Holder} holder
 *
 * @param {Props} props
 */
function DocumentsCard({
  title,
  userId,
  documents: _documents,
  customerDocuments,
  mutateCustomerDocuments,
  holder,
}) {
  const holderId = useRouterNumber("holderId");

  const documents = useMemo(() => {
    return _documents.filter((document) => {
      if (
        document.holder_type !== holder.type &&
        document.holder_type !== null // cas de tous les holders types
      ) {
        return false;
      }
      return true;
    });
  }, [_documents, holder.type]);

  const [isCollapsed, setIsCollapsed] = useState(
    holderId === userId
      ? false
      : documents.length > MAX_DOCUMENTS_INITIALLY_DISPLAYED,
  );
  const intl = useIntl();

  const handleClickChevron = useCallback(() => {
    setIsCollapsed((isCollapsed) => !isCollapsed);
  }, []);

  const { headerHeight } = useHeader();

  /** @type {import("react").MutableRefObject<HTMLDivElement | null>} */
  const cardRef = useRef(null);

  useEffect(() => {
    if (holderId === userId && headerHeight !== 0) {
      cardRef.current?.scrollIntoView();
    }
  }, [headerHeight, holderId, userId]);

  return (
    <ContentBox ref={cardRef} isHighlighted={holderId === userId}>
      <HStack alignItems="flex-start">
        <Text fontSize="1.125rem" fontWeight={400} flexGrow={1}>
          {title}
        </Text>

        {documents.length > MAX_DOCUMENTS_INITIALLY_DISPLAYED && (
          <Button
            onClick={handleClickChevron}
            colorScheme="brandPrimary"
            variant="outline"
            rightIcon={
              <Icon
                as={isCollapsed ? IoChevronDownOutline : IoChevronUpOutline}
              />
            }>
            {isCollapsed
              ? intl.formatMessage({ defaultMessage: "Afficher" })
              : intl.formatMessage({ defaultMessage: "Masquer" })}
          </Button>
        )}
      </HStack>

      <HStack>
        {documents.length === 0 && (
          <Text colorScheme="gray" mt="1rem">
            {intl.formatMessage({
              defaultMessage: "Aucun document à afficher pour le moment",
            })}
          </Text>
        )}
      </HStack>

      <CollapseAnimation
        in={
          documents.length > 0 &&
          (!isCollapsed ||
            documents.length <= MAX_DOCUMENTS_INITIALLY_DISPLAYED)
        }
        initial={false}>
        <Box pt="1.5rem">
          <CustomersDocumentsTable
            userType="holder"
            userId={userId}
            documents={documents}
            customerDocuments={customerDocuments}
            mutateCustomerDocuments={mutateCustomerDocuments}
          />
        </Box>
      </CollapseAnimation>
    </ContentBox>
  );
}

export default DocumentsCard;
